/**
 * ModalPhoneNumberInput Component
 *
 * This component renders a phone number input field with an integrated country selector modal.
 * It is fully compatible with Formik forms and supports numeric validation, regex-based phone
 * number validation, and localization.
 *
 * Features:
 * - Displays a phone number input field with a floating label.
 * - Shows a clear input button (optional).
 * - Restricts input to numeric characters if `numericOnly` is true.
 * - Validates input against a selected country's regex when provided.
 * - Supports default values and default selected countries.
 * - Opens a modal to select countries with search functionality.
 * - Updates Formik field values and errors automatically.
 * - Dispatches selected country to Redux store for global access.
 * - Supports localization using react-i18next.
 *
 * Props (ModalPhoneNumberInputProps):
 * - name (string): The name of the input field (used by Formik).
 * - label (string): Label displayed above the input.
 * - placeholder (string): Placeholder text for the input.
 * - required (boolean): Marks the input as required.
 * - wrapperClassName (string): Custom class for the input wrapper.
 * - countriesList (array): Array of country objects for the modal selector.
 * - numericOnly (boolean): Restrict input to numeric characters only.
 * - clearInput (boolean): Clears the input when true.
 * - getInputCountry (function): Callback called when a country is selected.
 * - defaultValue (string): Default value of the input field.
 * - defaultSelectedItem (object): Default selected country.
 * - isTelephoneNumber (boolean): Determines whether regex validation should be applied.
 * - getRegexError (function): Callback to notify parent component of regex validation errors.
 * - disabled (boolean): Disables the input field.
 * - onClear (function): Callback executed when the input is cleared.
 * - autoFocus (boolean): Focuses the input on mount.
 * - hideClearButton (boolean): Hides the clear button if true.
 *
 * Usage:
 * <Formik initialValues={{ phone: '' }} onSubmit={handleSubmit}>
 *   <ModalPhoneNumberInput
 *      name="phone"
 *      label="Phone Number"
 *      countriesList={countries}
 *      numericOnly
 *      defaultSelectedItem={countries[0]}
 *      getInputCountry={(country) => console.log(country)}
 *   />
 * </Formik>
 *
 * Notes:
 * - The component integrates tightly with Formik, updating field values and errors.
 * - Supports localization and dynamic country selection.
 * - Designed to work seamlessly with both controlled and uncontrolled forms.
 */

import { Field, FieldProps, useFormikContext } from "formik";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { setCountryCode } from "../../redux/slices/authSlice";
import {
  ModalPhoneNumberInputCountry,
  ModalPhoneNumberInputProps,
} from "../../types";
import {
  ArabicCode,
  BASE_Images_API_URL,
  BASE_LOCAL_IMAGES_DIR,
} from "../../utils/constants/CommonConstants";
import { X_IMG } from "../../utils/constants/Images";
import SearchInput from "../SearchComponents/SearchInput";
import GeneralImage from "./GeneralImage";
import Modal from "./Modal";

const ModalPhoneNumberInput: React.FC<ModalPhoneNumberInputProps> = ({
  name,
  label,
  placeholder = "",
  required = false,
  wrapperClassName,
  countriesList = [],
  numericOnly = false,
  clearInput,
  getInputCountry,
  defaultValue,
  defaultSelectedItem,
  isTelephoneNumber,
  getRegexError,
  disabled,
  onClear,
  autoFocus,
  hideClearButton,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { setFieldValue, setFieldError } = useFormikContext();
  //?================States=====================
  const [showNumbersPicker, setShowNumbersPicker] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(defaultValue || "");
  const [selectedCountry, setSelectedCountry] =
    useState<ModalPhoneNumberInputCountry | null>(defaultSelectedItem || null);
  const [regexMessageError, setRegexMessageError] = useState<string>("");
  const { values } = useFormikContext<any>();

  useEffect(() => {
    if (values[name] && values[name] !== inputValue) {
      setInputValue(values[name]);
    }
  }, [values[name]]);
  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: any,
    numericOnly: boolean = false,
  ) => {
    const newInputValue = event.target.value;

    //? Allow only numeric input if numericOnly is true
    if (numericOnly && !/^\d*$/.test(newInputValue)) {
      setRegexMessageError(t("only_numeric_allowed"));
      if (getRegexError) {
        getRegexError(true);
      }
      setFieldError(name, t("only_numeric_allowed"));
      return;
    }

    //?============Check if the input has 0 digits================
    if (newInputValue.length === 0) {
      setInputValue(""); // Clear local state
      setFieldValue(name, ""); // Clear Formik field value
      setRegexMessageError(""); // Clear error messages
      if (getRegexError) getRegexError(false);
      if (onClear) onClear();
      return; // Return early to prevent overwriting the cleared state
    }

    //?============Update the values================
    setInputValue(newInputValue);
    setFieldValue(name, newInputValue);

    //? Check if the input has at least 9 digits
    if (newInputValue.length >= 9) {
      if (selectedCountry?.Regex && !isTelephoneNumber) {
        const regex = new RegExp(selectedCountry.Regex);

        if (regex.test(newInputValue)) {
          setRegexMessageError(""); // Clear error if input matches regex
          if (getRegexError) getRegexError(false);
          setFieldError(name, ""); // Clear Formik error if input matches regex
        } else {
          const errorMessage = t("invalid_phone_regex");
          setRegexMessageError(errorMessage); // Show error if input doesn't match regex
          if (getRegexError) getRegexError(true);
          setFieldError(name, errorMessage); // Set Formik error
        }
      } else {
        setRegexMessageError(""); // Clear error for no regex or validation
        // if (getRegexError) getRegexError(false);
        setFieldError(name, "");
      }
    } else {
      setRegexMessageError(""); // Clear error for input < 9 digits
      // if (getRegexError) getRegexError(false);
      setFieldError(name, "");
    }

    field?.onChange(event); // Update the form field value
  };

  //?===================Country Selector =============================
  const handleCountrySelect = (country: ModalPhoneNumberInputCountry) => {
    if (getInputCountry !== undefined) {
      getInputCountry(country);
    }

    if (selectedCountry !== country) {
      setSelectedCountry(country);
      dispatch(
        setCountryCode({
          countryId: country.Id,
          countryData: country,
        }),
      );
      setRegexMessageError("");

      setInputValue("");
      setFieldValue(name, "");
    }
    setShowNumbersPicker(false);
  };
  //?===================On Paste Handler =================
  const handlePaste = (e: any) => {
    const pastedData = e.clipboardData.getData("text");
    if (!/^\d+$/.test(pastedData)) {
      e.preventDefault();
      setRegexMessageError(t("only_numeric_allowed"));
    } else {
      setRegexMessageError("");
    }
  };

  //?=================handle Press On Clear Input=============
  const handlePressOnClearInput = () => {
    setInputValue("");
    setFieldValue(name, "");
    setRegexMessageError("");
    if (getRegexError) getRegexError(false);
    if (onClear) onClear();
  };
  useLayoutEffect(() => {
    if (countriesList.length > 0 && !selectedCountry) {
      const defaultCountry: any = countriesList[0];
      setSelectedCountry(defaultCountry);
      dispatch(
        setCountryCode({
          countryId: defaultCountry?.Id,
          countryData: defaultCountry,
        }),
      );
      getInputCountry?.(defaultCountry);
    }
  }, [countriesList]);

  //?==============Clear Input Sensors =================
  useEffect(() => {
    if (clearInput) {
      setInputValue("");
    }
  }, [clearInput]);

  useEffect(() => {
    if (defaultSelectedItem) {
      setSelectedCountry(defaultSelectedItem);
      getInputCountry?.(defaultSelectedItem, name);
    }
  }, [defaultSelectedItem]);

  useEffect(() => {
    if (defaultValue !== undefined) {
      setFieldValue(name, defaultValue);
    }
  }, [defaultValue]);
  return (
    <>
      <div
        className={`${wrapperClassName} relative flex items-center rounded-xl px-1 py-1`}
      >
        <label
          htmlFor={name}
          className={`absolute -top-2 left-2 bg-white px-1 text-sm transition-all duration-200 ${
            inputValue ? "text-blue-500" : "text-gray-500"
          }`}
          style={{
            transform: inputValue ? "translateY(-50%)" : "translateY(0)",
          }}
        >
          {label}
        </label>
        <Field name={name} className="outline-none">
          {({ field, meta }: FieldProps) => (
            <div className="flex w-full flex-col gap-1">
              <input
                type="text"
                id={name}
                required={required}
                className={`w-full rounded-lg bg-inputBg py-4 pe-8 ps-28 text-base text-textColor placeholder-textColor shadow-login_input focus:outline-primaryMain`}
                placeholder={placeholder}
                {...field}
                value={inputValue}
                defaultValue={defaultValue}
                disabled={disabled}
                onKeyPress={(e) => {
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onPaste={(e) => {
                  handlePaste(e);
                }}
                onChange={(e) => handleInputChange(e, field, numericOnly)}
                onBlur={field.onBlur}
              />
              {!hideClearButton
                ? inputValue && (
                    <img
                      src={`${BASE_LOCAL_IMAGES_DIR}/${X_IMG}`}
                      alt="Clear"
                      loading="lazy"
                      className={`absolute top-6 w-5 cursor-pointer ${
                        i18n.language === ArabicCode ? "left-6" : "right-6"
                      }`}
                      onClick={() => {
                        if (!disabled) {
                          handlePressOnClearInput();
                        }
                      }}
                    />
                  )
                : null}
              {regexMessageError && (
                <span className="font max-w-[15rem] text-wrap text-sm font-semibold text-errorMain">
                  {regexMessageError}
                </span>
              )}
              {/* //?===========Countries List Indicatior=============== */}
              {countriesList && countriesList.length > 0 && (
                <div
                  className={`absolute top-3 flex cursor-pointer items-center rounded-xl bg-white px-2 py-2 ${
                    i18n.language === ArabicCode ? "right-3" : "left-3"
                  }`}
                  onClick={() => setShowNumbersPicker(true)}
                >
                  <span className="flex items-center gap-1 text-gray-500">
                    <GeneralImage
                      src={`${BASE_Images_API_URL}/${selectedCountry?.Flag}`}
                      className="h-6 w-7 object-cover"
                      loading="lazy"
                    />
                    <span
                      className="font-semibold text-headingTextColor"
                      dir="ltr"
                    >
                      {selectedCountry?.ZipCode}
                    </span>
                  </span>
                </div>
              )}
              {meta.touched && meta.error && (
                <p className="mt-1 text-sm font-semibold text-errorMain">
                  {meta.error}
                </p>
              )}
            </div>
          )}
        </Field>
      </div>
      {showNumbersPicker && !disabled && (
        <Modal
          isOpen={showNumbersPicker}
          height="h-1/2"
          onClose={() => {
            setShowNumbersPicker(false);
          }}
          disableCancel={true}
        >
          <div className="flex w-full flex-col rounded-3xl bg-white">
            <p className="px-4 py-6 text-lg font-bold text-headingTextColor">
              {t("countries")}
            </p>
            <SearchInput
              searchList={countriesList}
              placeholder={t("search_for_countries")}
              onSelectCountry={handleCountrySelect}
            />
          </div>
        </Modal>
      )}
    </>
  );
};

export default ModalPhoneNumberInput;
